import { toBlob } from 'html-to-image';

export interface AskAiChatImagePayload {
  contentType: 'image/png';
  data: string;
}

const SLICE_HEIGHT = 4000;

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read the chat image.'));
    reader.readAsDataURL(blob);
  });
}

function prepareSnapshot(dialog: HTMLElement): { stage: HTMLElement; clone: HTMLElement } {
  const thread = dialog.querySelector('[data-ask-thread]') as HTMLElement | null;
  const threadHeight = Math.max(thread?.scrollHeight || 0, thread?.offsetHeight || 0);
  const width = Math.max(1, Math.ceil(dialog.getBoundingClientRect().width));
  const clone = dialog.cloneNode(true) as HTMLElement;
  clone.removeAttribute('id');
  clone.setAttribute('aria-hidden', 'true');
  clone.style.width = `${width}px`;
  clone.style.height = 'auto';
  clone.style.maxHeight = 'none';
  clone.style.overflow = 'visible';
  clone.style.position = 'relative';
  clone.style.left = '0';
  clone.style.top = '0';
  const cloneThread = clone.querySelector('[data-ask-thread]') as HTMLElement | null;
  if (cloneThread) {
    cloneThread.style.height = `${Math.max(threadHeight, 1)}px`;
    cloneThread.style.maxHeight = 'none';
    cloneThread.style.overflow = 'visible';
    cloneThread.style.flex = '0 0 auto';
  }
  const stage = document.createElement('div');
  stage.setAttribute('aria-hidden', 'true');
  stage.style.cssText = `position:fixed;left:-10000px;top:0;width:${width}px;pointer-events:none;background:#ffffff;z-index:-1;`;
  stage.appendChild(clone);
  document.body.appendChild(stage);
  return { stage, clone };
}

async function captureNode(node: HTMLElement, width: number, height: number): Promise<Blob> {
  const blob = await toBlob(node, {
    backgroundColor: '#ffffff',
    pixelRatio: 1,
    skipFonts: true,
    cacheBust: true,
    width,
    height,
    canvasWidth: width,
    canvasHeight: height,
  });
  if (!blob || blob.size < 32) {
    throw new Error('Could not capture the chat image.');
  }
  return blob;
}

export async function captureAskAiChatImages(dialog: HTMLElement): Promise<AskAiChatImagePayload[]> {
  const { stage, clone } = prepareSnapshot(dialog);
  try {
    await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
    const width = Math.max(1, Math.ceil(clone.scrollWidth || clone.getBoundingClientRect().width));
    const total = Math.max(1, Math.ceil(clone.scrollHeight || clone.getBoundingClientRect().height));
    const blobs: Blob[] = [];
    if (total <= SLICE_HEIGHT) {
      blobs.push(await captureNode(clone, width, total));
    } else {
      const windowEl = document.createElement('div');
      windowEl.style.cssText = `width:${width}px;overflow:hidden;background:#ffffff;position:relative;`;
      stage.appendChild(windowEl);
      clone.style.position = 'absolute';
      clone.style.left = '0';
      windowEl.appendChild(clone);
      for (let top = 0; top < total; top += SLICE_HEIGHT) {
        const height = Math.min(SLICE_HEIGHT, total - top);
        windowEl.style.height = `${height}px`;
        clone.style.top = '0';
        clone.style.transform = `translateY(-${top}px)`;
        blobs.push(await captureNode(windowEl, width, height));
      }
    }
    const images: AskAiChatImagePayload[] = [];
    for (const blob of blobs) {
      images.push({ contentType: 'image/png', data: await blobToBase64(blob) });
    }
    if (images.length === 0) {
      throw new Error('Could not capture the chat image.');
    }
    return images;
  } finally {
    stage.remove();
  }
}
